import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getCompany,
  updateCompany,
  getBatches,
  getBankStatementData,
  getCompanyKnowledge,
  getCompanyNominalCodes,
  getCompanyCounterparties,
  createCounterparty,
  updateCounterparty,
  deleteCounterparty
} from '../services/api';
import { Company, Batch, Knowledge, NominalCode, Counterparty, CounterpartyType, CreateCounterpartyRequest } from '../types/api';

type TabType = 'overview' | 'batches' | 'bank-statements' | 'counterparties' | 'knowledge' | 'nominal-codes';

export default function CompanyDetail() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Overview state
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    business_name: '',
    registration_number: '',
    vat_number: '',
    base_currency: 'GBP'
  });
  const [saving, setSaving] = useState(false);

  // Batches state
  const [batches, setBatches] = useState<Batch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  // Bank statements state
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [bankLoading, setBankLoading] = useState(false);

  // Knowledge state
  const [knowledge, setKnowledge] = useState<Knowledge[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);

  // Nominal codes state
  const [nominalCodes, setNominalCodes] = useState<NominalCode[]>([]);
  const [nominalLoading, setNominalLoading] = useState(false);

  // Counterparties state
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [counterpartiesLoading, setCounterpartiesLoading] = useState(false);
  const [counterpartyFilter, setCounterpartyFilter] = useState<'all' | 'supplier' | 'customer'>('all');
  const [showCounterpartyModal, setShowCounterpartyModal] = useState(false);
  const [editingCounterparty, setEditingCounterparty] = useState<Counterparty | null>(null);
  const [counterpartyForm, setCounterpartyForm] = useState<CreateCounterpartyRequest>({
    company_id: 0,
    type: 'supplier',
    account_name: '',
    account_reference: '',
    contact_name: '',
    email: '',
    phone: '',
    fax: '',
    street_1: '',
    street_2: '',
    town: '',
    country: '',
    postcode: '',
    vat_number: '',
    notes: ''
  });
  const [savingCounterparty, setSavingCounterparty] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadCompany();
    }
  }, [companyId]);

  useEffect(() => {
    if (company) {
      loadTabData();
    }
  }, [activeTab, company]);

  const loadCompany = async () => {
    try {
      const data = await getCompany(Number(companyId));
      setCompany(data);
      setFormData({
        company_name: data.company_name || '',
        business_name: data.business_name || '',
        registration_number: data.registration_number || '',
        vat_number: data.vat_number || '',
        base_currency: data.base_currency || 'GBP'
      });
    } catch (error) {
      console.error('Failed to load company:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async () => {
    switch (activeTab) {
      case 'batches':
        if (batches.length === 0) {
          setBatchesLoading(true);
          try {
            const data = await getBatches({ company_id: Number(companyId) });
            setBatches(data);
          } catch (error) {
            console.error('Failed to load batches:', error);
          } finally {
            setBatchesLoading(false);
          }
        }
        break;
      case 'bank-statements':
        if (bankAccounts.length === 0) {
          setBankLoading(true);
          try {
            const data = await getBankStatementData(Number(companyId));
            setBankAccounts(data.accounts || []);
          } catch (error) {
            console.error('Failed to load bank statements:', error);
          } finally {
            setBankLoading(false);
          }
        }
        break;
      case 'knowledge':
        if (knowledge.length === 0) {
          setKnowledgeLoading(true);
          try {
            const data = await getCompanyKnowledge(Number(companyId));
            setKnowledge(data.knowledge || []);
          } catch (error) {
            console.error('Failed to load knowledge:', error);
          } finally {
            setKnowledgeLoading(false);
          }
        }
        break;
      case 'nominal-codes':
        if (nominalCodes.length === 0) {
          setNominalLoading(true);
          try {
            const data = await getCompanyNominalCodes(Number(companyId));
            setNominalCodes(data);
          } catch (error) {
            console.error('Failed to load nominal codes:', error);
          } finally {
            setNominalLoading(false);
          }
        }
        break;
      case 'counterparties':
        if (counterparties.length === 0) {
          setCounterpartiesLoading(true);
          try {
            const data = await getCompanyCounterparties(Number(companyId));
            setCounterparties(data);
          } catch (error) {
            console.error('Failed to load counterparties:', error);
          } finally {
            setCounterpartiesLoading(false);
          }
        }
        break;
    }
  };

  const handleSave = async () => {
    if (!company) return;
    setSaving(true);
    try {
      await updateCompany(company.id, formData);
      await loadCompany();
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update company:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (company) {
      setFormData({
        company_name: company.company_name || '',
        business_name: company.business_name || '',
        registration_number: company.registration_number || '',
        vat_number: company.vat_number || '',
        base_currency: company.base_currency || 'GBP'
      });
    }
    setIsEditing(false);
  };

  const resetCounterpartyForm = () => {
    setCounterpartyForm({
      company_id: Number(companyId),
      type: 'supplier',
      account_name: '',
      account_reference: '',
      contact_name: '',
      email: '',
      phone: '',
      fax: '',
      street_1: '',
      street_2: '',
      town: '',
      country: '',
      postcode: '',
      vat_number: '',
      notes: ''
    });
    setEditingCounterparty(null);
  };

  const handleOpenCounterpartyModal = (counterparty?: Counterparty) => {
    if (counterparty) {
      setEditingCounterparty(counterparty);
      setCounterpartyForm({
        company_id: counterparty.company_id,
        type: counterparty.type,
        account_name: counterparty.account_name,
        account_reference: counterparty.account_reference || '',
        contact_name: counterparty.contact_name || '',
        email: counterparty.email || '',
        phone: counterparty.phone || '',
        fax: counterparty.fax || '',
        street_1: counterparty.street_1 || '',
        street_2: counterparty.street_2 || '',
        town: counterparty.town || '',
        country: counterparty.country || '',
        postcode: counterparty.postcode || '',
        vat_number: counterparty.vat_number || '',
        notes: counterparty.notes || ''
      });
    } else {
      resetCounterpartyForm();
    }
    setShowCounterpartyModal(true);
  };

  const handleSaveCounterparty = async () => {
    setSavingCounterparty(true);
    try {
      if (editingCounterparty) {
        await updateCounterparty(editingCounterparty.id, counterpartyForm);
      } else {
        await createCounterparty({ ...counterpartyForm, company_id: Number(companyId) });
      }
      // Reload counterparties
      const data = await getCompanyCounterparties(Number(companyId));
      setCounterparties(data);
      setShowCounterpartyModal(false);
      resetCounterpartyForm();
    } catch (error) {
      console.error('Failed to save counterparty:', error);
    } finally {
      setSavingCounterparty(false);
    }
  };

  const handleDeleteCounterparty = async (counterpartyId: number) => {
    if (!confirm('Are you sure you want to delete this counterparty?')) return;
    try {
      await deleteCounterparty(counterpartyId);
      setCounterparties(counterparties.filter(c => c.id !== counterpartyId));
    } catch (error) {
      console.error('Failed to delete counterparty:', error);
    }
  };

  const filteredCounterparties = counterparties.filter(c =>
    counterpartyFilter === 'all' || c.type === counterpartyFilter
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl text-gray-600">Company not found</h2>
          <button
            onClick={() => navigate('/companies')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  const tabs: { id: TabType; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'batches', label: 'Batches', count: batches.length },
    { id: 'bank-statements', label: 'Bank Statements', count: bankAccounts.length },
    { id: 'counterparties', label: 'Counterparties', count: counterparties.length },
    { id: 'knowledge', label: 'Knowledge', count: knowledge.length },
    { id: 'nominal-codes', label: 'Nominal Codes', count: nominalCodes.length }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center text-sm text-gray-500 mb-2">
          <Link to="/companies" className="hover:text-blue-600">Companies</Link>
          <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-900">{company.company_name}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">{company.company_name}</h1>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              company.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {company.status}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Company Information</h2>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            {isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration Number
                  </label>
                  <input
                    type="text"
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VAT Number
                  </label>
                  <input
                    type="text"
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base Currency
                  </label>
                  <select
                    value={formData.base_currency}
                    onChange={(e) => setFormData({ ...formData, base_currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="GBP">GBP - British Pound</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - US Dollar</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{company.company_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Business Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{company.business_name || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Registration Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{company.registration_number || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">VAT Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{company.vat_number || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Base Currency</dt>
                  <dd className="mt-1 text-sm text-gray-900">{company.base_currency || 'GBP'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {company.created_at ? new Date(company.created_at).toLocaleDateString() : '-'}
                  </dd>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'batches' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Batches</h2>
            <Link
              to={`/batches?company=${companyId}`}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View All in Batches
            </Link>
          </div>

          {batchesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No batches found for this company.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Files</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {batches.map((batch) => (
                    <tr
                      key={batch.id}
                      onClick={() => navigate(`/batches/${batch.id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">Batch #{batch.id}</div>
                        {(batch.si_ref || batch.pi_ref) && (
                          <div className="text-xs text-gray-500">
                            {batch.si_ref && `SI: ${batch.si_ref}`}
                            {batch.si_ref && batch.pi_ref && ' | '}
                            {batch.pi_ref && `PI: ${batch.pi_ref}`}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(batch.status)}`}>
                          {batch.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {batch.files_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {batch.progress !== undefined && (
                          <div className="flex items-center">
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden mr-2">
                              <div
                                className="h-full bg-blue-500"
                                style={{ width: `${batch.progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">{batch.progress}%</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(batch.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'bank-statements' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Bank Accounts</h2>
            <Link
              to={`/bank-statements?company=${companyId}`}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View Bank Statements
            </Link>
          </div>

          {bankLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : bankAccounts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No bank accounts found for this company.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bankAccounts.map((account: any) => (
                    <tr key={account.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {account.account_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account.account_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account.currency || 'GBP'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.balance !== undefined
                          ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: account.currency || 'GBP' }).format(account.balance)
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'counterparties' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold">Counterparties</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCounterpartyFilter('all')}
                  className={`px-3 py-1 text-sm rounded-full ${
                    counterpartyFilter === 'all'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All ({counterparties.length})
                </button>
                <button
                  onClick={() => setCounterpartyFilter('supplier')}
                  className={`px-3 py-1 text-sm rounded-full ${
                    counterpartyFilter === 'supplier'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Suppliers ({counterparties.filter(c => c.type === 'supplier').length})
                </button>
                <button
                  onClick={() => setCounterpartyFilter('customer')}
                  className={`px-3 py-1 text-sm rounded-full ${
                    counterpartyFilter === 'customer'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Customers ({counterparties.filter(c => c.type === 'customer').length})
                </button>
              </div>
            </div>
            <button
              onClick={() => handleOpenCounterpartyModal()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Counterparty
            </button>
          </div>

          {counterpartiesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredCounterparties.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {counterparties.length === 0
                ? 'No counterparties found for this company.'
                : `No ${counterpartyFilter}s found.`}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Ref</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCounterparties.map((cp) => (
                    <tr key={cp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          cp.type === 'supplier'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {cp.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {cp.account_reference || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {cp.account_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {cp.contact_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {cp.phone || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          cp.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {cp.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleOpenCounterpartyModal(cp)}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCounterparty(cp.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Counterparty Modal */}
      {showCounterpartyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingCounterparty ? 'Edit Counterparty' : 'Add New Counterparty'}
              </h3>
              <button
                onClick={() => {
                  setShowCounterpartyModal(false);
                  resetCounterpartyForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={counterpartyForm.type}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, type: e.target.value as CounterpartyType })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="supplier">Supplier</option>
                    <option value="customer">Customer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Reference</label>
                  <input
                    type="text"
                    value={counterpartyForm.account_reference}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, account_reference: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., SUP001"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
                  <input
                    type="text"
                    required
                    value={counterpartyForm.account_name}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, account_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Company or individual name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={counterpartyForm.contact_name}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, contact_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Primary contact person"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                  <input
                    type="text"
                    value={counterpartyForm.vat_number}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, vat_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={counterpartyForm.phone}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
                  <input
                    type="tel"
                    value={counterpartyForm.fax}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, fax: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address 1</label>
                  <input
                    type="text"
                    value={counterpartyForm.street_1}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, street_1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address 2</label>
                  <input
                    type="text"
                    value={counterpartyForm.street_2}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, street_2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Town/City</label>
                  <input
                    type="text"
                    value={counterpartyForm.town}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, town: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                  <input
                    type="text"
                    value={counterpartyForm.postcode}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, postcode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={counterpartyForm.country}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={counterpartyForm.notes}
                    onChange={(e) => setCounterpartyForm({ ...counterpartyForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCounterpartyModal(false);
                  resetCounterpartyForm();
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCounterparty}
                disabled={savingCounterparty || !counterpartyForm.account_name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {savingCounterparty ? 'Saving...' : editingCounterparty ? 'Update Counterparty' : 'Create Counterparty'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'knowledge' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Knowledge Base</h2>
            <Link
              to={`/knowledge?company=${companyId}`}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Manage Knowledge
            </Link>
          </div>

          {knowledgeLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : knowledge.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No knowledge entries found for this company.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {knowledge.map((entry) => (
                <div key={entry.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{entry.title}</h3>
                      <p className="mt-1 text-sm text-gray-500 line-clamp-2">{entry.notes}</p>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-400">
                        <span className="text-gray-500">by {entry.author}</span>
                        <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'nominal-codes' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Nominal Codes</h2>
            <Link
              to={`/nominal-codes?company=${companyId}`}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Manage Nominal Codes
            </Link>
          </div>

          {nominalLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : nominalCodes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No nominal codes found for this company.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VAT Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {nominalCodes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {code.nominal_code}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {code.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {code.category || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {code.vat_rate !== undefined ? `${code.vat_rate}%` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          code.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {code.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
